import { useMemo } from 'react';

import type { ActiveContent } from '@atlassian/ai-audio/types/active-content';

import { getURLBypassingResumeDraftAction } from '@confluence/content-utils/entry-points/resumeDraftActionBypass.ts';
import type { EditorPropertyType } from '@confluence/content-utils/entry-points/EditorPropertyType.ts';

import type { useAudioContentQuery$data } from '../components/platform/__generated__/useAudioContentQuery.graphql.ts';

type UseActiveContentProps = {
	contentId: string;
	contentType: string;
	isLivePage?: boolean;
	spaceKey?: string;
	data: useAudioContentQuery$data | undefined;
	title?: string;
};

export const useActiveContent = ({
	contentId,
	contentType,
	isLivePage,
	spaceKey = '',
	data,
	title: currentTitle,
}: UseActiveContentProps): ActiveContent => {
	const title = currentTitle || (data?.singleContent?.title as string);
	const ownerName = data?.singleContent?.history?.ownedBy?.displayName;
	const authorName = data?.singleContent?.history?.createdBy?.displayName;
	const ownerAvatarUrl = data?.singleContent?.history?.ownedBy?.profilePicture?.path;
	const authorAvatarUrl = data?.singleContent?.history?.createdBy?.profilePicture?.path;
	const baseLink = data?.singleContent?.links?.base || '';
	const webuiLink = data?.singleContent?.links?.webui || '';

	const shouldUsePageOwner = ownerName && contentType === 'page';
	const contentAvatarName = shouldUsePageOwner ? ownerName : authorName;
	const contentAvatarUrl = shouldUsePageOwner ? ownerAvatarUrl : authorAvatarUrl;

	const editorVersion = data?.singleContent?.properties?.nodes
		?.find((prop) => prop?.key === 'editor')
		?.value?.replace(/['"]+/g, '') as EditorPropertyType;

	const mainContentLink = useMemo(() => {
		return editorVersion === 'v2'
			? baseLink +
					getURLBypassingResumeDraftAction({
						url: webuiLink,
						spaceKey,
						contentType,
						editorVersion,
						contentId,
						noContextPath: true,
					})
			: baseLink + webuiLink;
	}, [editorVersion, spaceKey, contentType, contentId, baseLink, webuiLink]);

	const emojiId = data?.singleContent?.properties?.nodes?.find(
		(prop) => prop?.key === 'emoji-title-published',
	)?.value;
	const subType = data?.singleContent?.subType;

	return useMemo(
		() => ({
			contentId,
			contentType,
			isLivePage,
			subType,
			spaceKey,
			title,
			author: contentAvatarName,
			avatarUrl: contentAvatarUrl,
			supportingContent: [],
			mainContentLink,
			emojiId,
		}),
		[
			contentId,
			contentType,
			isLivePage,
			subType,
			spaceKey,
			title,
			contentAvatarName,
			contentAvatarUrl,
			emojiId,
			mainContentLink,
		],
	);
};
